<template>
  <v-carousel
    class="transparent-carousel"
    vertical
    hide-delimiters
    :hide-controls="filteredCards.length <= 1"
    interval="40000"
    :cycle="true"
  >
    <v-carousel-item v-for="(itms, index) in filteredCards" :key="index">
      <v-layout row nowrap>
        <v-flex v-for="item in itms" :key="item.name" xs2>
          <Card class="card-content header-card">
            <div class="card-title">{{ $t(item.title) }}</div>
            <div class="card-value">
              {{ item.value }}
              <span v-if="item.unit" class="card-unit">{{ item.unit }}</span>
            </div>
          </Card>
        </v-flex>
      </v-layout>
    </v-carousel-item>
  </v-carousel>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import { mixin, mapGettersMixin } from "@/util/mixin/index";
import headerList from "../../services/StaticInfo.js";
import Card from "@/components/common/Cards.vue";
var moment = require("moment");

export default {
  mixins: [mixin, mapGettersMixin],
  components: {
    Card,
  },
  data() {
    return {
      plant_count: 0,
      ac_capacity: 0,
      dc_capacity: 0,
      list: [],
      energyMap: {},
    };
  },
  computed: {
    ...mapGetters([
      "isPlantExpired",
      "getAcCapacity",
      "getDcCapacity",
      "getTotalPlantCount",
      "getGeneralState",
      "getPlantList",
    ]),
    todayCo2() {
      return this.energyMap.todayTotalEnergy
        ? (this.energyMap.todayTotalEnergy * 0.392) / 1000
        : 0;
    },
    todayTreeSaved() {
      return this.energyMap.todayTotalEnergy
        ? this.energyMap.todayTotalEnergy * 0.0117
        : 0;
    },
    allCards() {
      const items = [];
      const f = this.$options.filters;
      if (this.list.includes("TOTAL PLANTS")) {
        items.push({
          title: "Total Plants",
          value: this.plant_count,
        });
      }

      if (this.list.includes("AC CAPACITY")) {
        items.push({
          title: "AC Capacity",
          value: f.numberWithCommas(f.value(this.ac_capacity)),
          unit: f.unit(this.ac_capacity, "ac"),
        });
      }

      if (this.list.includes("DC CAPACITY")) {
        items.push({
          title: "DC Capacity",
          value: f.numberWithCommas(f.value(this.dc_capacity)),
          unit: f.unit(this.dc_capacity, "dc"),
        });
      }

      const periods = [
        {
          label: "Today",
          dataKey: "todayTotalEnergy",
          listCheck: "ENERGY TODAY",
        },
        {
          label: "Yesterday",
          dataKey: "yesterdayTotalEnergy",
          listCheck: null,
        },
        { label: "This Month", dataKey: "monthTotalEnergy", listCheck: null },
        { label: "This Year", dataKey: "yearTotalEnergy", listCheck: null },
      ];

      periods.forEach((period) => {
        if (period.listCheck && !this.list.includes(period.listCheck)) {
          return;
        }
        const energyValue = this.energyMap[period.dataKey];

        items.push({
          title: `Energy ${period.label}`,
          value: f.numberWithCommas(f.value(energyValue)),
          unit: f.unit(energyValue, "totalGen"),
        });

        const treeFactor = energyValue * 0.0117;
        const treeDecimal = this.countIntegersBeforeDecimal(treeFactor);
        const treeLifetime = this.convertToPower(treeFactor, treeDecimal);

        items.push({
          title: `Trees Planted ${period.label}`,
          value: treeLifetime.includes("*")
            ? `${treeLifetime.split("*")[0].trim()}`
            : treeLifetime,
          unit: "Nos",
        });

        const co2Factor = (energyValue * 0.392) / 1000;
        const co2Decimal = this.countIntegersBeforeDecimal(co2Factor);
        const co2Lifetime = this.convertUnit(co2Factor, co2Decimal, "Tons");

        items.push({
          title: `Eco Savings ${period.label}`,
          value: co2Lifetime.value.toFixed(2),
          unit: co2Lifetime.unit,
        });
      });

      return items;
    },
    filteredCards() {
      const clonedItems = _.cloneDeep(this.allCards);
      return this.groupArray(clonedItems, 6);
    },
    plantIdList() {
      return this.getPlantList
        .filter((el) => el.plantType === 0)
        .map((el) => el.id);
    },
  },
  mounted() {
    this.ac_capacity = this.getAcCapacity;
    this.dc_capacity = this.getDcCapacity;
    this.plant_count = this.getTotalPlantCount;
    this.setIntervalWithoutDelay(this.callAllApi);
  },
  methods: {
    ...mapMutations(["SET_LAST_CALL_TIME", "SET_TOTAL_GENERATION"]),
    groupArray(array, groupSize) {
      const groupedArray = [];
      for (let i = 0; i < array.length; i += groupSize) {
        groupedArray.push(array.slice(i, i + groupSize));
      }
      return groupedArray;
    },
    callAllApi() {
      let updateTime = moment().format("MM-DD-YYYY H:MM:SS");
      this.SET_LAST_CALL_TIME(updateTime);
      this.getHeaderList();
    },
    async getHeaderList() {
      let response = await headerList.getHeaderInfo({
        plantList: this.plantIdList.filter((id) => !this.isPlantExpired(id)),
      });
      let componentList = response.data.message;
      this.energyMap = response.data.energy;
      this.list = componentList.split(",");
    },
    countIntegersBeforeDecimal(number) {
      const stringRepresentation = number.toString();
      const indexOfDecimal = stringRepresentation.indexOf(".");

      if (indexOfDecimal !== -1) {
        return indexOfDecimal;
      } else {
        return stringRepresentation.length;
      }
    },
    convertUnit(value, decimalCount, unit) {
      let computedUnit = "";
      if (decimalCount >= 6 && decimalCount < 8) {
        value = value / 1000;
        computedUnit = "Kilo";
      }

      if (decimalCount >= 8 && decimalCount < 10) {
        value = value / 1000000;
        computedUnit = "Mega";
      }

      if (decimalCount >= 10) {
        value = value / 1000000000;
        computedUnit = "Giga";
      }

      computedUnit =
        computedUnit == "" ? unit : `${computedUnit}${unit.toLowerCase()}`;
      return {
        value: value,
        unit: computedUnit,
      };
    },
    convertToPower(value, decimalCount) {
      if (decimalCount >= 6) {
        const numbersBeforeDecimal = decimalCount - 3;
        return `${(value / Math.pow(10, numbersBeforeDecimal)).toFixed(0)}`;
      } else {
        return value.toFixed(0);
      }
    },
  },
  watch: {
    isReload() {
      if (this.isReload) {
        this.setIntervalWithoutDelay(this.callAllApi);
      }
    },
    "energyMap.todayTotalEnergy"(val) {
      this.SET_TOTAL_GENERATION(val);
    },
  },
};
</script>

<style scoped>
.header-container {
  width: 100%;
}

.header-card {
  border-radius: 10px;
  box-shadow: 1px 2px 2px rgba(126, 158, 247, 0.418);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.header-card:hover {
  transform: translateY(-3px);
  box-shadow: 1px 2px 10px rgba(74, 122, 255, 0.7);
}

.cards-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: 8px;
}

.transparent-carousel {
  height: 5.9rem !important;
  border: none;
  box-shadow: none;
}

.card-content {
  padding: 12px !important;
  text-align: center;
  border-radius: 10px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.card-title {
  font-size: 1.12rem;
  font-weight: bold;
  color: #dadada;
  margin-bottom: 4px;
}

.theme--light .card-title {
  color: var(--v-extraGrey-base);
}

.card-value {
  font-size: 20px;
  font-weight: bold;
  color: #4a7aff;
}

.card-unit {
  font-size: 12px;
  color: #a0a9ff;
  font-weight: normal;
}

@media screen and (max-width: 1200px) {
  .cards-grid {
    grid-template-columns: 1fr 1fr 1fr 1ff;
  }
}

@media screen and (max-width: 510px) {
  .transparent-carousel {
    height: 9rem !important;
  }

  .cards-grid {
    grid-template-columns: 1fr 1fr;
  }

  .card-content {
    padding: 4px !important;
  }

  .card-title {
    font-size: 14px;
  }

  .card-value {
    font-size: 16px;
  }

  .card-unit {
    font-size: 8px;
  }
}

@media (max-width: 1600px) and (min-width: 1200px) {
  .transparent-carousel {
    height: 7.7rem !important;
  }
}

@media (max-width: 1200px) and (min-width: 1000px) {
  .transparent-carousel {
    height: 7.2rem !important;
  }

  .cards-grid {
    grid-template-columns: 1fr 1fr 1fr;
  }

  .card-content {
    padding: 8px !important;
  }

  .card-title {
    font-size: 16px;
  }

  .card-value {
    font-size: 18px;
  }

  .card-unit {
    font-size: 10px;
  }
}

@media (max-width: 1000px) {
  .transparent-carousel {
    height: 8.7rem !important;
  }
}
</style>
